import { FeatureFilterItem, FeatureFilterItemType } from './FeatureFilterItemType';
import { updateFeaturesValue, updateTagsValue } from '../../mutators/capabilityFilterMutators';
import { getStore } from '../../store/store';
import { useClientStyles } from 'owa-react-hooks/lib/useClientStyles';
import { observer } from 'mobx-react-lite';
import { Checkbox, ICheckboxProps } from '@fluentui/react/lib/Checkbox';
import { ComboBox, IComboBoxOption } from '@fluentui/react/lib/ComboBox';
import { DropdownMenuItemType } from '@fluentui/react/lib/Dropdown';
import { Icon } from '@fluentui/react/lib/Icon';
import type { ITheme } from '@fluentui/react/lib/Styling';
import { ControlIcons } from 'owa-control-icons';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import React from 'react';
import {
    featureFilterAudioCheckboxLabel,
    featureFilterDisplayCheckboxLabel,
    featureFilterVideoCheckboxLabel,
    featureFilterAccessibleLabel,
    featureFilterLabel,
} from './CapabilityFilters.locstring.json';
import { useModeStyles } from 'owa-react-hooks/lib/useModeStyles';

const styles = {
    base: require('./CapabilityFilters.scss'),
    desktop: require('./CapabilityFilters.outlookdesktop.scss'),
    mac: require('./CapabilityFilters.macoutlook.scss'),
};

const FEATURE_ITEMS: FeatureFilterItem[] = [
    {
        feature: FeatureFilterItemType.Audio,
        icon: ControlIcons.Volume2,
        label: loc(featureFilterAudioCheckboxLabel),
    },
    {
        feature: FeatureFilterItemType.Display,
        icon: ControlIcons.TVMonitor,
        label: loc(featureFilterDisplayCheckboxLabel),
    },
    {
        feature: FeatureFilterItemType.Video,
        icon: ControlIcons.Video,
        label: loc(featureFilterVideoCheckboxLabel),
    },
    {
        feature: FeatureFilterItemType.Accessible,
        icon: ControlIcons.EaseOfAccess,
        label: loc(featureFilterAccessibleLabel),
    },
];

export interface FeatureFilterProps {
    theme: ITheme;
    hasFeatures: boolean[];
    placeholder: string;
    disabled?: boolean;
    onChange?: () => void;
}

export default observer(function FeatureFilter(props: FeatureFilterProps) {
    const { getClass } = useClientStyles(styles);
    const { getModeAwareClass } = useModeStyles(getClass);

    const renderFeatureFilterItem = (item: FeatureFilterItem, index: number) => {
        const selectedFeatures = getStore().roomFilterState.selectedFeatures;
        const isEnabled = !props.disabled && props.hasFeatures[index];

        let isSelected = isEnabled && selectedFeatures.includes(item.feature);

        return (
            <div
                key={item.label}
                data-selection-index={index}
                data-selection-toggle={true}
                role="listitem">
                <Checkbox
                    // create checkbox class name in format of "checkbox-x-feature" where "x" is the index
                    // of checkbox in the list, and "feature" is the feature name
                    className={getClass('featureCheckbox')}
                    aria-Label={item.feature}
                    inputProps={{ role: 'checkbox' }}
                    key={index}
                    checked={isSelected}
                    onChange={onCheckboxChange(item)}
                    onRenderLabel={onRenderCheckboxLabel(item, isEnabled)}
                    disabled={!isEnabled}
                />
            </div>
        );
    };

    const onRenderCheckboxLabel = (
        item: FeatureFilterItem,
        isEnabled: boolean
    ): ((props: ICheckboxProps) => JSX.Element) => {
        const { theme } = props;
        return (props: ICheckboxProps) => {
            return (
                <div
                    // apply disabled text color accordingly fabric checkbox does not pass disabled style to customize-rendered label
                    style={{
                        color: isEnabled
                            ? theme.semanticColors.bodyText
                            : theme.semanticColors.disabledText,
                    }}>
                    <Icon className={getClass('featureFilterIcon')} iconName={item.icon} />
                    <span className={getClass('featureFilterLabel')}>{item.label}</span>
                </div>
            );
        };
    };

    const onCheckboxChange = (item: FeatureFilterItem) => {
        return (ev?: any, isChecked?: boolean) => {
            // Create a non observable copy of the selected features array
            const selectedFeatures = getStore().roomFilterState.selectedFeatures?.slice() || [];

            // Update selected features in the local array
            if (isChecked) {
                selectedFeatures.push(item.feature);
            } else {
                selectedFeatures.splice(selectedFeatures.indexOf(item.feature), 1);
            }

            // copy the new array to store
            updateFeaturesValue(selectedFeatures);

            props.onChange?.();
        };
    };

    const renderFeatureFilterOption = (item: FeatureFilterItem): IComboBoxOption => {
        const selectedFeatures = getStore().roomFilterState.selectedFeatures;
        const isEnabled = props.hasFeatures[item.feature];

        const isSelected = isEnabled && selectedFeatures.includes(item.feature);
        return {
            key: item.feature,
            text: item.label,
            disabled: !isEnabled,
            selected: isSelected,
            data: { icon: item.icon },
        };
    };

    const renderTagFilterOption = (tag: string, index: number): IComboBoxOption => {
        const selectedTags = getStore().roomFilterState.selectedTags;
        return {
            key: `tag_${tag}_${index}`,
            text: tag,
            selected: selectedTags ? selectedTags.includes(tag) : false,
            data: { icon: null, isFeatureTagOption: true },
        };
    };

    const onComboBoxChange = (event: any, option?: IComboBoxOption) => {
        if (option.data?.isFeatureTagOption) {
            const selectedTags = getStore().roomFilterState.selectedTags?.slice() || [];
            if (option.selected) {
                selectedTags.push(option.text);
            } else {
                selectedTags.splice(selectedTags.indexOf(option.text), 1);
            }
            updateTagsValue(selectedTags);
        } else {
            const selectedFeatures = getStore().roomFilterState.selectedFeatures?.slice() || [];
            if (option.selected) {
                selectedFeatures.push(option.key as FeatureFilterItemType);
            } else {
                selectedFeatures.splice(
                    selectedFeatures.indexOf(option.key as FeatureFilterItemType),
                    1
                );
            }

            updateFeaturesValue(selectedFeatures);
        }
        if (props.onChange) {
            props.onChange();
        }
    };

    const onRenderComboBoxLabel = (item: IComboBoxOption) => {
        return (
            <span
                style={{
                    color: item.disabled
                        ? props.theme.semanticColors.disabledText
                        : props.theme.semanticColors.bodyText,
                }}>
                <Icon className={getClass('featureFilterIcon')} iconName={item.data?.icon} />
                <span className={getClass('featureFilterLabel')}>{item.text}</span>
            </span>
        );
    };

    if (isFeatureEnabled('cal-mf-advancedRoomBookingV2')) {
        let itemOptions = FEATURE_ITEMS.map(renderFeatureFilterOption);
        if (isFeatureEnabled('cal-mf-usePlacesVNext')) {
            const tagsIndexKeys = [...getStore().roomListViewState.tagsIndex.keys()];
            if (tagsIndexKeys.length > 0) {
                itemOptions.push({
                    key: 'divider_1',
                    text: '-',
                    itemType: DropdownMenuItemType.Divider,
                });
                itemOptions = itemOptions.concat(tagsIndexKeys.map(renderTagFilterOption));
            }
        }

        return (
            <ComboBox
                openOnKeyboardFocus={true}
                multiSelect={true}
                options={itemOptions}
                onChange={onComboBoxChange}
                useComboBoxAsMenuWidth={true}
                onRenderOption={onRenderComboBoxLabel}
                placeholder={props.placeholder}
                styles={{
                    root: getClass('featureFilterComboBox'),
                    rootPressed: getClass('featureFilterComboBoxActive'),
                    rootFocused: getClass('featureFilterComboBoxActive'),
                    input: getModeAwareClass('featureFilterInput'),
                }}
                comboBoxOptionStyles={{
                    root: getClass('featureFilterOptionRoot'),
                }}
                caretDownButtonStyles={{
                    root: getClass('featureFilterCaretDownButton'),
                }}
                ariaLabel={loc(featureFilterLabel)}
            />
        );
    }
    return <>{FEATURE_ITEMS.map(renderFeatureFilterItem)}</>;
});
